import React, { useEffect, useMemo, useState } from 'react';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import { useCourse } from '../contexts/CourseContext';
import { useAuth } from '../contexts/AuthContext';
import { productNav } from '../components/NavigationPath';
import Loader from '../components/Loader';
import Layout2 from '../components/Layout2';
import AddCard from '../components/AddCard';
import AddCourse from '../components/AddCourse';
import PageHeader from '../components/PageHeader';
import InfiniteScroll from 'react-infinite-scroll-component';

function CoursesList() {
    const { getAllCourses, getSingleCourse } = useCourse();
    const { toast, restaurantId, auth, hasPermission } = useAuth();

    const canCreate = useMemo(() => hasPermission("products", "create"),  [hasPermission]);
    const canUpdate = useMemo(() => hasPermission("products", "update"), [hasPermission]);
    const canDelete = useMemo(() => hasPermission("products", "delete"), [hasPermission]);

    const [coursesList, setCoursesList] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editCourseId, setEditCourseId] = useState(null);
    const [totalRecords, setTotalRecords] = useState(0);
    const [totalRenderedRecords, setTotalRenderedRecords] = useState(0);
    const [hasMore, setHasMore] = useState(true);
    const [courseDetail, setCourseDetail] = useState({
        name: '',
        restaurant_id: restaurantId
    })
    const [filter, setFilter] = useState({
        limit: 50,
        filter: null,
        restaurant_id: restaurantId,
        totalRenderedRecords: 0,
    });

    useEffect(() => {
        if (restaurantId) {
            handleFilterChange({ ...filter, restaurant_id: restaurantId })
        }
    }, [restaurantId])

    const handleFilterChange = async (newFilter) => {
        setCoursesList([]);
        setFilter({ ...newFilter, totalRenderedRecords: 0, });
        setHasMore(true);
    };

    useEffect(() => {
        if (filter) {
            fetchCourses(filter);
        }
    }, [filter])

    const fetchCourses = async (filter) => {
        try {
            if (restaurantId) {
                setIsLoading(true);

                const data = await getAllCourses(filter);
                if (data.courses.length !== 0 && data.totalCount !== coursesList.length) {
                    setCoursesList([...coursesList, ...data.courses]);
                    setTotalRecords(data.totalCount)
                    setTotalRenderedRecords(coursesList.length)
                }

                setIsLoading(false);
            }
        } catch (error) {
            console.error('Error fetching courses', error);
        }
    };

    const handleCardClick = (id) => {
        if(!canUpdate && !canDelete) return;
        setEditMode(true);
        setEditCourseId(id);
        setVisible(false);
    };

    const handleLoadMore = () => {
        if (coursesList.length === totalRecords) {
            setHasMore(false);
        } else if (coursesList.length === filter.limit) {
            const updatedFilter = { ...filter, totalRenderedRecords: filter.limit };

            setFilter(updatedFilter);
            setTotalRenderedRecords(updatedFilter.totalRenderedRecords);
        } else {
            const updatedFilter = { ...filter, totalRenderedRecords: totalRenderedRecords + filter.limit };

            setFilter(updatedFilter);
            setTotalRenderedRecords(updatedFilter.totalRenderedRecords);
        }
    };

    const fetchCourseProps = async (newCourseId) => {
        try {
            if (editMode && editCourseId) {
                const course = await getSingleCourse(editCourseId);
                const courseIndex = coursesList.findIndex((u) => u.id === editCourseId);

                if (courseIndex !== -1) {
                    if (!course) {
                        const updatedCourseList = [...coursesList];
                        updatedCourseList.splice(courseIndex, 1);

                        setCoursesList(updatedCourseList);
                        setTotalRecords(totalRecords - 1);
                        setTotalRenderedRecords(totalRenderedRecords - 1)
                    } else {
                        const updatedCourseList = [...coursesList];
                        updatedCourseList[courseIndex] = course;

                        setCoursesList(updatedCourseList);
                    }
                } else {
                    setCoursesList([...coursesList, course]);
                }
            } else if (visible) {
                const updatedCourseList = [...coursesList];
                updatedCourseList.unshift({ ...courseDetail, id: newCourseId });

                setCoursesList(updatedCourseList);
                setTotalRecords(totalRecords + 1);
                setTotalRenderedRecords(totalRenderedRecords + 1)
            }
        } catch (error) {
            console.error('Error fetching courses.', error);
        }
    };

    const renderCardsInRows = () => {
        const rows = [];
        let currentRow = [];

        coursesList && coursesList.forEach((course, index) => {
            currentRow.push(
                <div key={course.id} className="d-flex">
                    <Card
                        header={
                            <>
                                <div className='modifier-name' title={course.name}>
                                    {course.name}
                                </div>
                            </>}
                        className="modifiers-list-card"
                        onClick={() => handleCardClick(course.id)}
                    />
                </div>
            );

            rows.push(
                <div key={index} className="d-flex">
                    {currentRow}
                </div>
            );
            currentRow = [];
        });

        return rows;
    };

    return (
        <Layout2 items={productNav.items} navTitle={productNav.header}>
            <div className="d-flex flex-column w-100">
                <PageHeader title={'Courses'} />
                <div className='px-4 py-2'>
                    {(visible || (editMode && editCourseId)) &&
                        <AddCourse
                            id={editCourseId}
                            visible={visible}
                            editCourseId={editCourseId}
                            editMode={editMode}
                            courseDetail={courseDetail}
                            setEditCourseId={setEditCourseId}
                            setVisible={setVisible}
                            setEditMode={setEditMode}
                            fetchCourses={fetchCourseProps}
                            setCourseDetail={setCourseDetail}
                            canCreate={canCreate}
                            canUpdate={canUpdate}
                            canDelete={canDelete}
                        />
                    }
                    {isLoading ? (
                        <Loader />
                    ) : (
                        <>
                            <Toast ref={toast} />
                            <div className="course-list">
                                <InfiniteScroll
                                    dataLength={coursesList.length}
                                    next={handleLoadMore}
                                    hasMore={hasMore}
                                    className="card-container"
                                >
                                    {canCreate && <AddCard color="#536347" label="Add Course" setVisible={setVisible} />}
                                    {coursesList.length !== 0 && renderCardsInRows()}
                                </InfiniteScroll>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </Layout2>
    )
}

export default CoursesList;
